import {
  Link,
  Outlet,
  createFileRoute,
  useNavigate,
  useParams,
  useRouterState,
} from "@tanstack/react-router";
import {
  ArrowLeft,
  Boxes,
  CheckSquare,
  Circle,
  Clock,
  Database,
  GitBranch,
  Grid3x3,
  Lightbulb,
  Map as MapIcon,
  MessageSquare,
  Network as NetworkIcon,
  PanelRightClose,
  PanelRightOpen,
  Radio,
  RadioTower,
  Settings,
  Target,
  Workflow,
  X,
} from "lucide-react";
import { useState } from "react";
import { z } from "zod";

import { ChatPanel } from "@/components/canvas/chat-panel";
import { EntitySlideOver } from "@/components/canvas/entity-slide-over";
import { SidebarUser } from "@/components/dravian/sidebar-user";
import { TimeRangeBar } from "@/components/dravian/time-range-bar";
import { TopBar } from "@/components/dravian/top-bar";
import { useWorkspaces } from "@/store/workspaces";

const searchSchema = z.object({
  entity: z.string().optional(),
  // Comma-separated list of entity IDs to spotlight across canvases.
  // Read by each Live*Canvas to dim everything else.
  focus: z.string().optional(),
  // Inclusive time range, ISO date strings (YYYY-MM-DD). Empty = unbounded.
  // Read by timeline, signals, geographic (recent-weighting), risk matrix.
  from: z.string().optional(),
  to: z.string().optional(),
});

export const Route = createFileRoute("/$workspace")({
  validateSearch: searchSchema,
  component: WorkspaceLayout,
});

const decideNav = [
  { label: "Decisions", icon: CheckSquare, to: "decisions" },
];

const visualizeNav = [
  { label: "Geographic", icon: MapIcon, to: "geographic" },
  { label: "Network", icon: NetworkIcon, to: "network" },
  { label: "Timeline", icon: Clock, to: "timeline" },
  { label: "Flow", icon: Workflow, to: "flow" },
  { label: "Scenario Tree", icon: GitBranch, to: "scenario-tree" },
  { label: "Risk Matrix", icon: Grid3x3, to: "risk" },
];

// Operate sits between Visualize and Data: it is neither an analytical view
// nor a data store, but live hardware the operator acts on.
const operateNav = [
  { label: "Devices", icon: RadioTower, to: "devices" },
];

const dataNav = [
  { label: "Entities", icon: Boxes, to: "entities" },
  { label: "Signals", icon: Radio, to: "signals" },
  { label: "Hypotheses", icon: Lightbulb, to: "hypotheses" },
  { label: "Scenarios", icon: GitBranch, to: "scenarios" },
  { label: "Ontologies", icon: NetworkIcon, to: "ontologies" },
];

const manageNav = [
  { label: "Data Ingestion", icon: Database, to: "data" },
  { label: "Settings", icon: Settings, to: "settings" },
];

function WorkspaceLayout() {
  const { workspace: id } = useParams({ strict: false }) as { workspace: string };
  const { getById } = useWorkspaces();
  const ws = getById(id);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { entity, focus } = Route.useSearch();
  const [chatOpen, setChatOpen] = useState(true);
  const navigate = useNavigate();
  const focusCount = focus ? focus.split(",").filter(Boolean).length : 0;
  // Routes that render their own scoped chat surface should suppress the
  // global workspace chat so the operator sees ONE conversation, not two.
  // Right now only the decision detail brings its own; extend as needed.
  const routeOwnsChat = /\/decisions\/[^/]+/.test(pathname);
  const clearFocus = () =>
    navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({ ...prev, focus: undefined }),
    });

  return (
    <div className="flex h-screen flex-col bg-background">
      <TopBar
        center={
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-[18px] font-semibold truncate">
                  {ws?.name ?? "Workspace"}
                </h1>
                {ws?.published && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 text-primary border border-primary/30 px-2 py-0.5 text-[10px] font-semibold tracking-wider uppercase">
                    <Circle className="h-1.5 w-1.5 fill-current" /> Published
                  </span>
                )}
              </div>
              {ws?.subdomain && (
                <div className="text-[12px] text-primary/80 font-mono">{ws.subdomain}</div>
              )}
            </div>
            <div className="ml-auto">
              <TimeRangeBar />
            </div>
          </div>
        }
      />

      <div className="flex flex-1 min-h-0">
        {/* Left nav */}
        <aside className="w-[224px] shrink-0 border-r border-border bg-sidebar flex flex-col">
          <div className="p-3">
            <Link
              to="/hq"
              className="flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-[12px] font-semibold uppercase tracking-[0.14em] text-foreground/85 hover:bg-surface-elevated hover:text-primary hover:border-primary/40 transition"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              HQ
            </Link>
          </div>
          <nav className="flex-1 overflow-auto px-2 space-y-4 scrollbar-thin">
            <NavGroup title="Decide" items={decideNav} workspaceId={id} pathname={pathname} />
            <NavGroup title="Visualize" items={visualizeNav} workspaceId={id} pathname={pathname} />
            <NavGroup title="Operate" items={operateNav} workspaceId={id} pathname={pathname} />
            <NavGroup title="Data" items={dataNav} workspaceId={id} pathname={pathname} />
            <NavGroup title="Manage" items={manageNav} workspaceId={id} pathname={pathname} />
          </nav>
          <div className="p-3 border-t border-border">
            <SidebarUser />
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0 flex">
          <div className="flex-1 min-w-0 min-h-0 overflow-y-auto scrollbar-thin relative">
            <Outlet />
            {focusCount > 0 && (
              <button
                onClick={clearFocus}
                className="absolute left-3 top-3 z-20 inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/15 text-primary px-2.5 py-1 text-[11px] font-semibold hover:bg-primary/25"
                title="Clear focus"
              >
                <Target className="h-3 w-3" /> Focus on {focusCount}{" "}
                <X className="h-3 w-3 opacity-70" />
              </button>
            )}
            {/* Re-open chat button when collapsed */}
            {!chatOpen && !routeOwnsChat && (
              <button
                onClick={() => setChatOpen(true)}
                className="absolute right-3 top-3 z-10 grid h-8 w-8 place-items-center rounded-md border border-border bg-surface/95 text-foreground/80 hover:bg-surface-elevated"
                title="Open chat"
              >
                <PanelRightOpen className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Right rail: persistent workspace chat. Suppressed when the child
              route provides its own scoped chat (e.g. a decision detail) so
              the operator never sees two chat panels at once. */}
          {chatOpen && !routeOwnsChat && (
            <aside className="w-[340px] shrink-0 border-l border-border bg-sidebar flex flex-col min-h-0">
              <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                <div className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.14em] uppercase text-muted-foreground">
                  <MessageSquare className="h-3.5 w-3.5" /> Chat
                </div>
                <button
                  onClick={() => setChatOpen(false)}
                  className="rounded p-1 text-muted-foreground hover:bg-surface-elevated hover:text-foreground"
                  title="Collapse chat"
                >
                  <PanelRightClose className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="flex-1 min-h-0">
                <ChatPanel workspaceId={id} />
              </div>
            </aside>
          )}
        </main>
      </div>

      {/* Slide-over entity inspector — triggered by ?entity=<id> */}
      <EntitySlideOver workspaceId={id} entityId={entity} />
    </div>
  );
}

function NavGroup({
  title,
  items,
  workspaceId,
  pathname,
}: {
  title: string;
  items: { label: string; icon: React.ComponentType<{ className?: string }>; to: string }[];
  workspaceId: string;
  pathname: string;
}) {
  return (
    <div>
      <div className="label-eyebrow px-2 mb-1.5">{title}</div>
      <div className="space-y-0.5">
        {items.map((item) => {
          const href = `/${workspaceId}/${item.to}`;
          const active = pathname === href || pathname.startsWith(href + "/");
          const Icon = item.icon;
          return (
            <Link
              key={item.to}
              to={href}
              className={`flex items-center gap-2.5 rounded-md px-3 py-1.5 text-[12.5px] transition ${
                active
                  ? "bg-primary/15 text-foreground border border-primary/25"
                  : "text-muted-foreground hover:text-foreground hover:bg-sidebar-accent border border-transparent"
              }`}
            >
              <Icon className={`h-3.5 w-3.5 ${active ? "text-primary" : ""}`} />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
